import { useState, useEffect } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { getLandingPage, saveLandingPage } from '../lib/services/landing-page.service';
import type { LandingPage } from '../lib/types';
import { 
  ArrowLeftIcon, 
  PaintBrushIcon, 
  DevicePhoneMobileIcon, 
  ComputerDesktopIcon,
  GlobeAltIcon,
  CloudArrowUpIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

export function LandingPageCanvas() {
  const { currentWorkspace } = useWorkspace();
  const [page, setPage] = useState<LandingPage | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');

  useEffect(() => {
    if (currentWorkspace) {
      setPage(getLandingPage(currentWorkspace.id));
    }
  }, [currentWorkspace]);

  const handleSave = () => {
    if (page) {
      setIsSaving(true);
      saveLandingPage(page);
      setTimeout(() => {
        setIsSaving(false);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      }, 500);
    }
  };

  if (!currentWorkspace || !page) return null;

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Canvas Header */}
      <header className="h-14 bg-white border-b flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-4">
          <a href="/" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeftIcon className="h-5 w-5 text-gray-500" />
          </a>
          <div className="h-6 w-px bg-gray-200" />
          <h1 className="font-semibold text-gray-900">Landing Page Canvas</h1>
          <span className="badge badge-info">LIG-1320</span>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 p-1 rounded-lg mr-4">
            <button 
              onClick={() => setPreviewMode('desktop')}
              className={`p-1.5 rounded-md transition-all ${previewMode === 'desktop' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <ComputerDesktopIcon className="h-4 w-4" />
            </button>
            <button 
              onClick={() => setPreviewMode('mobile')}
              className={`p-1.5 rounded-md transition-all ${previewMode === 'mobile' ? 'bg-white shadow-sm text-primary-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <DevicePhoneMobileIcon className="h-4 w-4" />
            </button>
          </div>
          
          <button 
            onClick={handleSave}
            disabled={isSaving}
            className="btn-primary btn-sm flex items-center gap-2"
          >
            {isSaving ? 'Saving...' : showSuccess ? (
              <>
                <CheckCircleIcon className="h-4 w-4" />
                Saved!
              </>
            ) : (
              <>
                <CloudArrowUpIcon className="h-4 w-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar Editor */}
        <aside className="w-80 border-r bg-white overflow-y-auto p-6 space-y-8 shrink-0">
          <section>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Branding</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-700">Business Name</label>
                <input 
                  type="text" 
                  value={currentWorkspace.name}
                  disabled
                  className="mt-1 w-full bg-gray-50 border border-gray-200 rounded-lg p-2 text-sm text-gray-500 cursor-not-allowed"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-700">Primary Color</label>
                  <div className="mt-1 flex gap-2">
                    <input 
                      type="color" 
                      value={page.primaryColor}
                      onChange={(e) => setPage({...page, primaryColor: e.target.value})}
                      className="h-8 w-8 rounded-lg border-0 p-0 cursor-pointer"
                    />
                    <input 
                      type="text" 
                      value={page.primaryColor}
                      onChange={(e) => setPage({...page, primaryColor: e.target.value})}
                      className="flex-1 text-xs border border-gray-200 rounded-lg px-2 uppercase"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-700">Accent Color</label>
                  <div className="mt-1 flex gap-2">
                    <input 
                      type="color" 
                      value={page.accentColor}
                      onChange={(e) => setPage({...page, accentColor: e.target.value})}
                      className="h-8 w-8 rounded-lg border-0 p-0 cursor-pointer"
                    />
                    <input 
                      type="text" 
                      value={page.accentColor}
                      onChange={(e) => setPage({...page, accentColor: e.target.value})}
                      className="flex-1 text-xs border border-gray-200 rounded-lg px-2 uppercase"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Hero Section</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-gray-700">Hero Title</label>
                <input 
                  type="text" 
                  value={page.title}
                  onChange={(e) => setPage({...page, title: e.target.value})}
                  className="mt-1 w-full border border-gray-200 rounded-lg p-2 text-sm"
                  placeholder="The headline of your site"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">Subtitle</label>
                <textarea 
                  rows={3}
                  value={page.heroSubtitle}
                  onChange={(e) => setPage({...page, heroSubtitle: e.target.value})}
                  className="mt-1 w-full border border-gray-200 rounded-lg p-2 text-sm"
                  placeholder="Tell them what you do"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700">CTA Button Text</label>
                <input 
                  type="text" 
                  value={page.heroCtaText}
                  onChange={(e) => setPage({...page, heroCtaText: e.target.value})}
                  className="mt-1 w-full border border-gray-200 rounded-lg p-2 text-sm"
                />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Features</h3>
            <div className="space-y-4">
              {page.features.map((feature, idx) => (
                <div key={feature.id} className="p-3 border rounded-lg bg-gray-50/50 space-y-2">
                  <div className="flex gap-2">
                    <input 
                      type="text" 
                      value={feature.icon}
                      onChange={(e) => {
                        const newFeatures = [...page.features];
                        newFeatures[idx].icon = e.target.value;
                        setPage({...page, features: newFeatures});
                      }}
                      className="w-10 text-center border rounded p-1"
                    />
                    <input 
                      type="text" 
                      value={feature.title}
                      onChange={(e) => {
                        const newFeatures = [...page.features];
                        newFeatures[idx].title = e.target.value;
                        setPage({...page, features: newFeatures});
                      }}
                      className="flex-1 text-sm border rounded p-1 font-semibold"
                    />
                  </div>
                  <textarea 
                    rows={2}
                    value={feature.description}
                    onChange={(e) => {
                      const newFeatures = [...page.features];
                      newFeatures[idx].description = e.target.value;
                      setPage({...page, features: newFeatures});
                    }}
                    className="w-full text-xs border rounded p-1"
                  />
                </div>
              ))}
            </div>
          </section>
        </aside>

        {/* Live Preview */}
        <main className="flex-1 bg-gray-200 p-8 overflow-y-auto flex items-start justify-center">
          <div 
            className={`bg-white shadow-2xl transition-all duration-500 overflow-hidden ${
              previewMode === 'desktop' ? 'w-full max-w-5xl rounded-xl' : 'w-80 rounded-[3rem] border-[8px] border-gray-900 aspect-[9/19]'
            }`}
          >
            {/* Website Content */}
            <div className="h-full overflow-y-auto bg-white">
              {/* Nav */}
              <nav className="p-6 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: page.primaryColor }}>
                    <div className="w-3 h-3 bg-white rounded-sm" />
                  </div>
                  <span className="font-bold text-gray-900">{currentWorkspace.name}</span>
                </div>
                {previewMode === 'desktop' && (
                  <div className="flex gap-6 text-sm font-medium text-gray-600">
                    <span className="cursor-pointer">Features</span>
                    <span className="cursor-pointer">About</span>
                    <span className="cursor-pointer">Contact</span>
                  </div>
                )}
                <button className="px-4 py-2 rounded-lg text-white text-sm font-bold" style={{ backgroundColor: page.primaryColor }}>
                  {page.heroCtaText}
                </button>
              </nav>

              {/* Hero */}
              <section className="py-20 px-10 text-center">
                <h2 className="text-4xl font-extrabold text-gray-900 mb-6 leading-tight">
                  {page.title}
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-10">
                  {page.heroSubtitle}
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button className="px-8 py-4 rounded-xl text-white font-bold shadow-lg shadow-primary-200" style={{ backgroundColor: page.primaryColor }}>
                    {page.heroCtaText}
                  </button>
                  <button className="px-8 py-4 rounded-xl bg-white border border-gray-200 text-gray-900 font-bold">
                    Learn More
                  </button>
                </div>
              </section>

              {/* Features */}
              <section className="py-20 px-10 bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {page.features.map((feature) => (
                    <div key={feature.id} className="p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow">
                      <div className="text-3xl mb-4">{feature.icon}</div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Stats / Proof */}
              <section className="py-20 px-10 text-center border-t border-gray-100">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                  <div>
                    <p className="text-3xl font-black text-gray-900" style={{ color: page.primaryColor }}>10k+</p>
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">Users</p>
                  </div>
                  <div>
                    <p className="text-3xl font-black text-gray-900" style={{ color: page.primaryColor }}>99.9%</p>
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">Uptime</p>
                  </div>
                  <div>
                    <p className="text-3xl font-black text-gray-900" style={{ color: page.primaryColor }}>24/7</p>
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">Support</p>
                  </div>
                  <div>
                    <p className="text-3xl font-black text-gray-900" style={{ color: page.primaryColor }}>4.9/5</p>
                    <p className="text-xs text-gray-500 font-bold uppercase tracking-widest mt-1">Rating</p>
                  </div>
                </div>
              </section>

              {/* Footer */}
              <footer className="p-10 border-t border-gray-100 flex flex-col items-center gap-4">
                <div className="flex gap-8 text-gray-400">
                  <GlobeAltIcon className="h-6 w-6" />
                  <PaintBrushIcon className="h-6 w-6" />
                </div>
                <p className="text-xs text-gray-400">© 2026 {currentWorkspace.name}. All rights reserved.</p>
              </footer>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default LandingPageCanvas;
